import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { ElMessage } from 'element-plus';
import { useAuthStore } from '@/stores/auth';
import { isWrappedApiResponse, type ApiResponse } from '@/types/api';

/**
 * 创建一个注入鉴权 + 统一错误处理的 axios 实例。
 *
 * 行为：
 * - 请求拦截器：从 auth store 读 token，注入 `Authorization: Bearer <token>`。
 * - 响应拦截器（成功分支）：宽松模式判定
 *   - 后端返回 {code,message,data} 且 code!==0 → ElMessage.error + reject
 *   - 否则按成功返回完整 AxiosResponse（业务代码自行 .data 取数据）
 * - 响应拦截器（错误分支）：
 *   - 401：清空 auth + 跳 /wb/login?redirect=...
 *   - 5xx：ElMessage 提示服务器错误
 *   - Network Error：提示网络异常
 *   - 其他：展示后端 message 或 err.message
 *
 * 偏差说明（与 PRD 第 3.1 节）：
 * 老 web-feedback 后端很多接口直接返回业务对象而非 ApiResponse 包装。
 * 本拦截器按「宽松模式」实现：响应体不含 code 字段时按成功处理。
 * 待 W2 后端整体改造后再收紧为严格模式。
 *
 * 循环依赖规避：
 * router 与 http.ts 互相引用会导致循环 import。这里用动态 import + 全局
 * router 实例延迟加载（懒加载在响应拦截器内部，运行时已经过 Pinia 注册）。
 */
export function createHttp(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  // ===== 请求拦截器：注入 Bearer token =====
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const auth = useAuthStore();
      if (auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`;
      }
      return config;
    },
    (err) => Promise.reject(err),
  );

  // ===== 响应拦截器 =====
  instance.interceptors.response.use(
    (resp: AxiosResponse) => {
      const body = resp.data;
      // 宽松模式：只有当响应体明显是 ApiResponse 包装（含 code 字段）时才校验
      if (isWrappedApiResponse(body)) {
        const wrapped = body as ApiResponse;
        if (wrapped.code !== 0) {
          const message = wrapped.message || '业务错误';
          ElMessage.error(message);
          return Promise.reject(new Error(message));
        }
      }
      return resp;
    },
    async (err) => {
      const status = err?.response?.status;
      const respBody = err?.response?.data;

      if (status === 401) {
        // 鉴权失效：清空 auth + 跳对应登录页（admin 页跳 /wb/admin/login，普通页跳 /wb/login）
        // D7：tender-sage 模块也共用这套登录
        //
        // P1-2 / audit-admin P2-15 / audit-login R-401a 修复（架构性）：
        // 老前端 auth.js:187-194 按 window.location.pathname 区分：
        //   - /admin*.html / invite-codes.html → 跳 /admin-login.html
        //   - 其他 → 跳 /login.html
        // 新 Portal 路由前缀：
        //   - /wb/admin/* → 管理员区域（admin 子页 + admin login）→ 跳 /wb/admin/login
        //   - 其他 → 跳 /wb/login（邀请码登录）
        // 避免管理员 token 失效被跳到邀请码登录页（admin 无法用账号密码登录）
        const auth = useAuthStore();
        auth.clear();
        // 动态 import 规避循环依赖
        const { default: router } = await import('@/router');
        const currentRoute = router.currentRoute.value;
        const current = currentRoute.fullPath;
        const isAdminArea = current.startsWith('/admin');
        const loginPath = isAdminArea ? '/admin/login' : '/login';
        // 避免在登录页重复跳登录页（同 path 时不重复触发跳转）
        if (currentRoute.path !== loginPath) {
          router.replace({
            path: loginPath,
            query: { redirect: current },
          });
        }
        ElMessage.warning('登录已失效，请重新登录');
      } else if (status && status >= 500) {
        ElMessage.error(`服务器错误（${status}），请稍后重试`);
      } else if (err?.message === 'Network Error') {
        ElMessage.error('网络异常，请检查连接');
      } else if (status === 429) {
        ElMessage.warning('请求过于频繁，请稍后再试');
      } else {
        // 4xx 其他 / 0（无 status）
        const message = respBody?.message || respBody?.detail || err?.message || '未知错误';
        ElMessage.error(message);
      }
      return Promise.reject(err);
    },
  );

  return instance;
}

/**
 * 解包辅助：从 AxiosResponse 提取业务数据。
 *
 * 宽松模式：
 * - 响应体是 ApiResponse 包装 → 返回 body.data
 * - 响应体是裸对象 → 返回 body 本身
 */
export function unwrap<T>(resp: AxiosResponse<T | ApiResponse<T>>): T {
  const body = resp.data;
  if (isWrappedApiResponse(body)) {
    return (body as ApiResponse<T>).data;
  }
  return body as T;
}
